import { morph } from '@theme/morph';
import { StandardEvents } from '@shopify/events';

export const PURCHASE_OPTION_CHANGE = 'simucube:purchase-option-change';

/** HI engine A/B test exp-296651c5: the class the storefront loader puts on <body> for the variant arm. */
const VARIANT_CLASS = 'hi-exp-exp-296651c5';

/**
 * @typedef {object} PurchaseOptionSelection
 * @property {string} variantId - The bundle variant that replaces the wheelbase line.
 * @property {number} price
 * @property {number} compare
 * @property {string} wheel - The bundle product handle.
 * @property {string} saveLabel - "Save [amount]" template for the bundle bar.
 */

/**
 * "Wheelbase only" / "With a steering wheel" choice in the PDP buy box.
 * Keeps `data-mode` in sync with the checked radios (CSS shows the wheel rows
 * in bundle mode, or always with `data-wheels-open`) and announces the choice
 * to the bundle bar with PURCHASE_OPTION_CHANGE. The bar also reads `selection` when it connects, so
 * the order the two modules load in does not matter.
 *
 * `selection` is null while the block is not rendered (display: none), and
 * unless <body> carries VARIANT_CLASS, so the copy hidden by the A/B gate never
 * changes the bar for the control group.
 */
class SimucubePurchaseOption extends HTMLElement {
  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {boolean} */
  #visible = false;

  /** @type {boolean} */
  #viewed = false;

  connectedCallback() {
    const { signal } = this.#abortController;

    this.addEventListener('change', this.#handleChange, { signal });
    this.closest('.shopify-section')?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, {
      signal,
    });

    // Fires once on observe and again whenever the block is shown or hidden.
    this.#resizeObserver = new ResizeObserver(() => {
      const visible = this.getClientRects().length > 0;
      if (visible === this.#visible) return;

      this.#visible = visible;
      if (visible) this.#trackView();
      this.#announce();
    });
    this.#resizeObserver.observe(this);

    this.#syncMode();
  }

  disconnectedCallback() {
    this.#abortController.abort();
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }

  /** @returns {PurchaseOptionSelection | null} */
  get selection() {
    if (!document.body.classList.contains(VARIANT_CLASS)) return null;
    if (!this.#visible || this.dataset.mode !== 'bundle') return null;

    const wheel = this.#checkedWheel();
    if (!wheel?.dataset.variantId) return null;

    const price = parseInt(wheel.dataset.price ?? '0', 10) || 0;
    return {
      variantId: wheel.dataset.variantId,
      price,
      compare: parseInt(wheel.dataset.compare ?? '0', 10) || price,
      wheel: wheel.dataset.wheel ?? '',
      saveLabel: this.dataset.saveLabel ?? '',
    };
  }

  /** @param {Event} event */
  #handleChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;

    if (input.hasAttribute('data-sc-po-wheel')) {
      // Picking a wheel row always means the bundle option.
      const bundleMode = this.querySelector('input[data-sc-po-mode][value="bundle"]');
      if (bundleMode instanceof HTMLInputElement) bundleMode.checked = true;
    } else if (input.hasAttribute('data-sc-po-mode') && input.value === 'bundle' && !this.#checkedWheel()) {
      const firstWheel = this.querySelector('input[data-sc-po-wheel]');
      if (firstWheel instanceof HTMLInputElement) firstWheel.checked = true;
    } else if (input.hasAttribute('data-sc-po-mode') && input.value === 'base') {
      // Back to the wheelbase alone: clear the wheel, so an open list never
      // shows (or announces to screen readers) a wheel as still chosen.
      const wheel = this.#checkedWheel();
      if (wheel) wheel.checked = false;
    }

    this.#syncMode();
    this.#syncChosen();
    this.#announce();

    const selection = this.selection;
    this.#push('sc_purchase_option_select', {
      sc_purchase_option: selection ? 'bundle' : 'base',
      sc_purchase_option_wheel: selection?.wheel ?? '',
    });
  };

  /**
   * The wheel rows depend on the selected wheelbase variant, so re-render
   * them from the section HTML after a variant change.
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;
        if (detail.productId && detail.productId !== this.dataset.productId) return;

        const next = detail.html.querySelector(
          `simucube-purchase-option[data-block-id="${this.dataset.blockId}"]`,
        );
        if (next) {
          this.dataset.mode = next.getAttribute('data-mode') ?? 'base';
          morph(this, next, { childrenOnly: true });
        } else {
          this.replaceChildren();
        }

        this.#syncMode();
        this.#syncChosen();
        this.#announce();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[simucube-purchase-option] Event promise rejected:', error);
      });
  };

  /** @returns {HTMLInputElement | null} */
  #checkedWheel() {
    const wheel = this.querySelector('input[data-sc-po-wheel]:checked');
    return wheel instanceof HTMLInputElement ? wheel : null;
  }

  #syncMode() {
    const mode = this.querySelector('input[data-sc-po-mode]:checked');
    if (mode instanceof HTMLInputElement) this.dataset.mode = mode.value;
  }

  /** The bundle card's header shows the picked wheel's price over its compare-at price (CSS hides it otherwise). */
  #syncChosen() {
    const wheel = this.#checkedWheel();
    const price = this.querySelector('[data-sc-po-chosen-price]');
    const compare = this.querySelector('[data-sc-po-chosen-compare]');
    if (price) price.textContent = wheel?.dataset.priceMoney ?? '';
    if (compare) compare.textContent = wheel?.dataset.compareMoney ?? '';
  }

  #announce() {
    this.dispatchEvent(new CustomEvent(PURCHASE_OPTION_CHANGE, { bubbles: true, detail: this.selection }));
  }

  #trackView() {
    if (this.#viewed || !document.body.classList.contains(VARIANT_CLASS)) return;
    this.#viewed = true;
    this.#push('sc_purchase_option_view', {});
  }

  /**
   * @param {string} name
   * @param {Record<string, string>} params
   */
  #push(name, params) {
    const layer = /** @type {{ dataLayer?: unknown[] }} */ (window).dataLayer;
    if (!Array.isArray(layer)) return;
    layer.push({ event: name, sc_product_id: this.dataset.productId ?? '', ...params });
  }
}

if (!customElements.get('simucube-purchase-option')) {
  customElements.define('simucube-purchase-option', SimucubePurchaseOption);
}
